import type { AssignedUserResource, UserResource } from '@repo/api-client';

/**
 * What a person's role and standing are called on screen.
 *
 * The wire values are `OWNER`, `MANAGER`, `OUTLET_STAFF` and `GATE_STAFF`, and
 * the API is the only thing that should ever see them. Nothing here restates the
 * union — it is read off `UserResource`, so a fifth role added to the document
 * fails to compile in this file rather than rendering as `SOMETHING_NEW` in an
 * owner's staffing list.
 */
export type UserRole = UserResource['role'];

/**
 * The roles, in the order the API's own enum declares them: most reach first.
 *
 * One order, used by the filter and by the create form both. Two orders — a
 * "safest first" one for creating and a "most reach first" one for filtering —
 * would put `OWNER` and `GATE_STAFF` in the same screen position on two controls
 * that sit inches apart, which is how somebody grants the wrong role in a hurry.
 */
export const USER_ROLES: readonly UserRole[] = ['OWNER', 'MANAGER', 'OUTLET_STAFF', 'GATE_STAFF'];

/**
 * The role a new person is offered before anybody chooses.
 *
 * Deliberately the least-privileged of the four, and deliberately not the first
 * item in the list above. A form that pre-selects `OWNER` because the API's enum
 * happens to declare it first turns a mis-submitted form into a second owner;
 * pre-selecting the narrowest role turns the same mistake into somebody who
 * cannot open a till, which is noticed immediately and costs a `PATCH`.
 */
export const DEFAULT_NEW_USER_ROLE: UserRole = 'GATE_STAFF';

/**
 * `Record` rather than a function with a default, so the exhaustiveness is the
 * type system's problem and not a runtime fallback nobody ever sees.
 */
const ROLE_LABELS: Record<UserRole, string> = {
  OWNER: 'Owner',
  MANAGER: 'Manager',
  OUTLET_STAFF: 'Outlet staff',
  GATE_STAFF: 'Gate staff',
};

export const labelForRole = (role: UserRole): string => ROLE_LABELS[role];

/**
 * Where somebody signs in, which decides the credential they cannot go without.
 *
 * A password opens the back office; a PIN names somebody at a till. Owners and
 * managers work at a desk, outlet and gate staff work at a counter, and the two
 * credentials are not interchangeable — a cashier with a password can reach
 * almost nothing (every back-office route but the dashboard is `OWNER`-only),
 * and a manager with only a PIN cannot sign in at all.
 *
 * **This exists because a person can be created with neither and nothing says
 * so.** The API accepts it: `CreateUserDto` marks both optional, correctly, for
 * the terminal-only worker who needs no password — but nothing then asserts that
 * what was supplied matches where the person works. A manager created with no
 * password is refused at sign-in with `AUTH_CREDENTIALS_INVALID`, the same code
 * as a wrong password, and each attempt counts toward the five-attempt lockout.
 * That happened. Reported as `blossom-rest-api#173`.
 *
 * So the rule here is a courtesy ahead of the server, and it is deliberately
 * about the role rather than "always require a password": forcing one on every
 * cashier would issue a back-office credential to somebody who has no business
 * holding one.
 */
export const signsInAtTheDesk = (role: UserRole): boolean => role === 'OWNER' || role === 'MANAGER';

/**
 * The roles as a value-to-label map, for `<Select items={…}>`.
 *
 * Base UI reads the trigger's label from that prop rather than from the rendered
 * `<SelectItem>`s, so a select given options but no `items` shows the raw value
 * — `OUTLET_STAFF` rather than "Outlet staff". Derived from `USER_ROLES` so the
 * picker and this cannot disagree.
 */
export const USER_ROLE_ITEMS: ReadonlyArray<{ label: string; value: UserRole }> = USER_ROLES.map(
  (role) => ({ label: labelForRole(role), value: role }),
);

/**
 * What each role can do, shown under the option when creating a person.
 *
 * Worded as reach rather than as a permission list. The person filling this form
 * is a park manager deciding who does what, not somebody auditing decorators,
 * and "manages the park's people and its setup" answers their question where
 * "OWNER" does not.
 */
const ROLE_HINTS: Record<UserRole, string> = {
  OWNER:
    'Sets the park up and manages its people. Reaches every outlet without being assigned one.',
  MANAGER:
    'Runs the day: prices, reports and the day close. Reaches every outlet, and does not manage people.',
  OUTLET_STAFF: 'Sells at a shop or an activity centre, at the outlets they are assigned to.',
  GATE_STAFF: 'Sells entry tickets and admits visitors, at the gates they are assigned to.',
};

export const hintForRole = (role: UserRole): string => ROLE_HINTS[role];

/**
 * Whether somebody has been deactivated.
 *
 * The nullable-timestamp annotation gap this was written around (`#209`) is
 * **closed** as of the 10 Aug 2026 regeneration: the field is a real
 * `string | null` and a screen may now say when. Nothing needs unwinding — the
 * predicate is still the right shape for the question it answers — but a screen
 * that wants the moment no longer has to pretend it cannot have it.
 */
export const isDeactivated = (user: Pick<UserResource, 'deactivatedAt'>): boolean =>
  user.deactivatedAt !== null && user.deactivatedAt !== undefined;

/** The same reading, for the lockout timestamp, which carries the same defect. */
export const isLockedOut = (user: Pick<UserResource, 'lockedUntil'>): boolean =>
  user.lockedUntil !== null && user.lockedUntil !== undefined;

/**
 * How somebody reaches the outlet the list is filtered to.
 *
 * `ROLE` is an owner or a manager, who reach every outlet without an assignment
 * row (FR-111); `ASSIGNMENT` is a row somebody created. Both are shown, because
 * "who can work here" and "who was assigned here" are different questions and a
 * roster that silently answered only the second would read as a gate with no
 * manager.
 */
export const labelForVia = (via: AssignedUserResource['via']): string =>
  via === 'ROLE' ? 'By role' : 'Assigned';

/**
 * **The staff-code and PIN shapes used to live here**, as `STAFF_CODE_SHAPE` and
 * `PIN_SHAPE` with a predicate each. They moved to `lib/field-schemas.ts` in
 * FE019, and this note is what is left of them.
 *
 * The reason is the reason that file exists: `PIN_MALFORMED` had a second copy
 * in `ChangeCredential.tsx`, written by somebody who could not see this one, and
 * nothing in `pnpm validate` could have noticed when the two drifted. A rule
 * about what a PIN is belongs where every form that sets one can reach it, not
 * in the folder of the two that happened to need it first.
 *
 * What stays here is what is genuinely about roles: the labels, the hints, the
 * order the four are offered in, and the readings of the API's nullable
 * timestamps.
 */
